import {useMutation, UseMutationOptions, useQuery, useQueryClient, UseQueryOptions} from '@tanstack/react-query';
import {extractResult} from '../utils/api';
import {BaseResponse} from "../models/baseResponse.ts";
import {PaginationData} from "../models/paginationData.ts";
import NoDataResponse from "../models/noDataResponse.ts";

/**
 * 기본 Query Hook
 */
export const useBaseQuery = <TData>(
    queryKey: readonly unknown[], // unknown[] → readonly unknown[]
    queryFn: () => Promise<TData>,
    options?: Omit<UseQueryOptions<TData, Error>, 'queryKey' | 'queryFn'>
) => {
    return useQuery<TData, Error>({
        queryKey,
        queryFn: async () => {
            return await queryFn();
        },
        ...options,
    });
};

/**
 * Pagination Query Hook
 */
export const usePaginationQuery = <TData>(
    queryKey: readonly unknown[], // unknown[] → readonly unknown[]
    queryFn: () => Promise<BaseResponse<PaginationData<TData>>>,
    options?: Omit<UseQueryOptions<PaginationData<TData>, Error>, 'queryKey' | 'queryFn'>
) => {
    return useQuery<PaginationData<TData>, Error>({
        queryKey,
        queryFn: async () => {
            const response = await queryFn();
            return extractResult(response);
        },
        ...options,
    });
};

/**
 * 기본 Mutation Hook
 */
export const useBaseMutation = <TVariables, TData>(
    mutationFn: (variables: TVariables) => Promise<TData>,
    options?: Omit<UseMutationOptions<TData, Error, TVariables>, 'mutationFn'>
) => {
    return useMutation<TData, Error, TVariables>({
        mutationFn: async (variables) => {
            return await mutationFn(variables);
        },
        ...options,
    });
};

/**
 * NoData Mutation Hook (삭제, 토글 등)
 */
export const useNoDataMutation = <TVariables>(
    mutationFn: (variables: TVariables) => Promise<NoDataResponse>,
    options?: Omit<UseMutationOptions<NoDataResponse, Error, TVariables>, 'mutationFn'>
) => {
    return useMutation<NoDataResponse, Error, TVariables>({
        mutationFn: async (variables) => {
            return await mutationFn(variables);
        },
        ...options,
    });
};

/**
 * Invalidate Query Helper
 */
export const useInvalidateQueries = () => {
    const queryClient = useQueryClient();

    return {
        // 특정 쿼리 무효화
        invalidate: (queryKey: readonly unknown[]) => { // unknown[] → readonly unknown[]
            queryClient.invalidateQueries({queryKey});
        },

        // 쿼리 키 prefix로 무효화
        invalidateByPrefix: (queryKeyPrefix: readonly unknown[]) => { // unknown[] → readonly unknown[]
            queryClient.invalidateQueries({
                predicate: (query) => {
                    const key = query.queryKey as unknown[];
                    return queryKeyPrefix.every((prefix, index) => key[index] === prefix);
                }
            });
        },

        // 여러 쿼리 동시 무효화
        invalidateMultiple: (queryKeys: readonly (readonly unknown[])[]) => { // unknown[][] → readonly (readonly unknown[])[]
            queryKeys.forEach(key => {
                queryClient.invalidateQueries({queryKey: key});
            });
        },
    };
};
